"use client"
import { useContext } from "react"
import {
  POLICY_ICONS,
  POLICY_STATUS_LABELS,
  POLICY_STATUS_ORDER,
  type Policy,
  type PolicyStatus,
} from "./policiesData"
import {
  POLICY_DESCRIPTIONS,
  POLICY_EXTENDED_DESCRIPTIONS,
} from "./policyDescriptions"
import { HUD87_POLICY_STATUS_APPEARANCE } from "./policies-panel/policyAppearance"
import { PolicyMiniSquare } from "./policyHoverSwap"
import { useGroupedPoliciesForActiveDate } from "./useGroupedPoliciesForActiveDate"
import {
  HudHoverDescription,
  HudHoverDetailBody,
  HudHoverRuleTreatmentContext,
  HudHoverTitle,
} from "../hudHoverDetailHeader"

// ─── The policy MAP hover (tabs 7–21) ────────────────────────────────────────
// One below-HUD visual for EVERY hover in the policy area — policy squares
// AND status labels. The whole policy list stays visible; the hovered item
// highlights in place and its definition shows in a reserved area. The
// designs vary the highlight vocabulary, where the definition lives, and how
// the list itself is laid out (see PolicyMapDesignId below).

// Which map design a tab uses — set per-variant via PolicyHoverSwap's
// mapDesign prop (variants.tsx):
//   "red-name"         — tab 7:  hovered name/header turns red (pure color)
//   "red-chip"         — tab 8:  hovered name/header inverts into a small red
//                                chip, white text (tab 3's metric-chip voice)
//   "mini-square"      — tab 9:  the policy's own mini square (status colors
//                                + icon) appears before the hovered name
//   "definition-right" — tab 10: definition in a fixed RIGHT panel beside the
//                                columns; hovered name gets a red underline
//   "headline-top"     — tab 11: definition as a headline block ABOVE the
//                                columns, over a red rule; hovered name reds
// Tabs 12–16 — tab 11's top placement pulled toward the hud-0 policy detail
// (status eyebrow, mini icon square beside the bold name, full extended
// description BELOW, no red rule), varying how the map underneath drops in
// hierarchy:
//   "hud0-map-small"   — tab 12: the map at a reduced type scale
//   "hud0-map-flat"    — tab 13: headers drop to name size (weight alone
//                                separates them) — the map reads as one block
//   "hud0-map-inline"  — tab 14: each section collapses to a single flowing
//                                line (header, then names run inline)
//   "hud0-big-square"  — tab 15: the detail's icon square at poster scale;
//                                the map keeps tab 11's sizes
//   "hud0-detail-large"— tab 16: the detail's type steps UP instead — the
//                                map is demoted by contrast
// Tabs 17–21 — tab 10's right-panel placement, attacking the skinny-column
// name wrapping:
//   "right-rows"       — tab 17: sections stack as full-width rows, names one
//                                per line — nothing is skinny
//   "right-inline"     — tab 18: sections stack, names flow inline (each
//                                nowrap) — wraps only BETWEEN names
//   "right-narrow"     — tab 19: narrower panel + smaller names = wider
//                                columns, milder wrapping
//   "right-hanging"    — tab 20: keeps tab 10's geometry but makes the wraps
//                                legible: hanging indent + inter-item space
//   "underline-bottom" — tab 21: the definition moves to a bottom strip so
//                                the columns get the full HUD width
// Tab 22 — "hud0-map-flat-lean": tab 13 slimmed — no status eyebrow above the
// name, and the description is the original one-liner only (no extended
// sentence).
// "hud0-lean-status-underline" — the lean design with the STATUS hover
// reworked: hovering a status row shows ONE broad "AI Policies" definition
// (POLICY_MAP_AI_POLICIES_COPY) instead of the per-status caption, and the
// hovered status's section in the map is marked by a red rule under its
// header — the power breakdown's underline emphasis, header text staying
// black. Policy hovers behave exactly like the lean design.
export type PolicyMapDesignId =
  | "red-name"
  | "red-chip"
  | "mini-square"
  | "definition-right"
  | "headline-top"
  | "hud0-map-small"
  | "hud0-map-flat"
  | "hud0-map-flat-lean"
  | "hud0-lean-status-underline"
  | "hud0-map-inline"
  | "hud0-big-square"
  | "hud0-detail-large"
  | "right-rows"
  | "right-inline"
  | "right-narrow"
  | "right-hanging"
  | "underline-bottom"

// Shared skeleton: the status columns and the reserved definition area.
// definitionMinHeightPx is held OPEN whenever the map is up, so the columns
// never move as the hovered item (and the length of its definition) changes.
export const POLICY_MAP_STYLE = {
  headerFontSize: "11px",
  nameFontSize: "8.5px",
  nameLineHeight: 1.6,
  columnGapPx: 12,
  headerGapPx: 2,
  definitionGapPx: 8,
  definitionMinHeightPx: 42,
  definitionNameFontSize: "11px",
  definitionFontSize: "10px",
  definitionLineHeight: 1.45,
}
export const POLICY_MAP_EMPTY_SECTION_MARK = "—"

// Statuses carry no description in the data — these captions fill the
// definition area when a status LABEL is the hovered thing.
export const POLICY_MAP_STATUS_CAPTIONS: Record<PolicyStatus, string> = {
  current: "Measures already in force at this point in the scenario.",
  in_discussion: "Measures on the table but not yet law.",
  off_the_table: "Measures currently ruled out by governments and labs.",
}

// Tab 7 "red-name" — pure color: the hovered name or section header turns
// red; everything else stays full black.
export const POLICY_MAP_RED_NAME_STYLE = {
  highlightColor: "var(--accent)",
}

// Tab 8 "red-chip" — inversion, the tab-3 metric-chip vocabulary: the hovered
// name or header sits on a rounded red chip with white text. Every row keeps
// the chip's padding permanently (transparent when idle, cancelled left by a
// negative margin so the column's text edge stays put) — no shift on hover.
export const POLICY_MAP_RED_CHIP_STYLE = {
  background: "var(--accent)",
  textColor: "#fff",
  borderRadiusPx: 3,
  paddingXPx: 4,
  paddingYPx: 1,
}

// Tab 9 "mini-square" — marker: the policy's own square (per-status colors,
// icon included) appears just before the hovered name, tying the map row back
// to the grid square under the pointer. Every row reserves the marker slot.
// A hovered status HEADER gets a plain marker in that section's border color.
export const POLICY_MAP_MINI_SQUARE_STYLE = {
  squareSizePx: 10,
  squareRadiusPx: 2,
  squareIconSizePx: 6.5,
  squareStrokeWidthPx: 1.2,
  slotWidthPx: 14,
}

// Tab 10 "definition-right" — placement: the columns keep the left of the
// slot and the definition lives in a fixed right panel (always present, so
// nothing reflows); the hovered name is marked by a red underline.
export const POLICY_MAP_DEFINITION_RIGHT_STYLE = {
  panelWidthPx: 190,
  panelGapPx: 20,
  underlineWidthPx: 1.5,
  underlineColor: "var(--accent)",
}

// Tab 11 "headline-top" — placement: the definition renders as a headline
// block ABOVE the columns (name at title size, description beside the map's
// voice), separated by a full-width red rule; the hovered name turns red.
export const POLICY_MAP_HEADLINE_TOP_STYLE = {
  nameFontSize: "12px",
  ruleWidthPx: 2,
  ruleColor: "var(--accent)",
  ruleGapPx: 5,
  columnsGapPx: 8,
  minHeightPx: 34,
  highlightColor: "var(--accent)",
}

// Tabs 12–16 — the shared "hud-0 detail on top" block: status eyebrow, mini
// icon square beside the bold name, full (extended) description below, NO
// red rule. detailMinHeightPx reserves the block's footprint so the map
// underneath never moves as the hover travels. (The name/description type
// itself is the shared HudHoverTitle / HudHoverDescription opener.)
export const POLICY_MAP_HUD0_TOP_STYLE = {
  detailMinHeightPx: 76,
}

// Tab 15 "hud0-big-square" — the detail's icon square at poster scale (the
// tab-23 big-square-aside voice), text column beside it.
export const POLICY_MAP_HUD0_BIG_SQUARE_STYLE = {
  squareSizePx: 28,
  squareRadiusPx: 6,
  squareIconSizePx: 16,
  squareStrokeWidthPx: 1.8,
  textGapPx: 9,
}

// Tab 22 "hud0-map-flat-lean" — tab 13 without the status eyebrow and with
// the short description only, so the reserved detail footprint shrinks too.
export const POLICY_MAP_HUD0_LEAN_STYLE = {
  detailMinHeightPx: 54,
}

// "hud0-lean-status-underline" — the broad definition shown for ANY status
// hover (made-up copy, edit freely), and the power-breakdown-matched rule
// that marks the hovered status's section header (its text stays black).
// The rule's slot is reserved under every header, so nothing shifts as the
// hover moves between rows.
export const POLICY_MAP_AI_POLICIES_COPY = {
  title: "AI Policies",
  // Kept short enough to sit on ONE line in the detail area (Joanna
  // 2026-07-11: trimmed "to consider" so it doesn't wrap).
  definition:
    "What governments and labs have enacted, are discussing, or have not begun.",
}
export const POLICY_MAP_STATUS_UNDERLINE_STYLE = {
  ruleWidthPx: 2,
  ruleColor: "var(--accent)",
  gapPx: 2,
  // The reserved detail footprint on this design: room for the shared 13px
  // title + a description that may wrap to TWO 13px lines, so the map never
  // shifts as the hover travels between short and long definitions.
  detailMinHeightPx: 58,
  // Non-stock rule treatments (Joanna 2026-07-11): the definition sits on one
  // line, so the map (the bottom section) rides higher (58 − 25).
  nonStockDetailMinHeightPx: 33,
}

// Tab 16 — the map's type matched to the dist-of-power breakdown (hierarchy
// mode) so the two hover areas read as one system: section headers mono bold
// 9.5px, policy names in the sans body face at 9.5px. Values mirror
// POWER_BREAKDOWN_HIERARCHY_STYLE as literals (importing belowHudDetailSwap
// here would be an import cycle) — keep them in sync by hand if the power
// side changes. (The title/description tier above the map is the shared
// HudHoverTitle / HudHoverDescription opener.)
export const POLICY_MAP_POWER_MATCHED_TYPE = {
  headerFontSize: "9.5px",
  nameFontSize: "9.5px",
}

// Tabs 14 / 17 / 18 — the stacked map layouts ("stacked-rows": each section a
// full-width block with names one per line; "stacked-inline": names run
// inline after the header, each name nowrap so lines break only BETWEEN
// names).
export const POLICY_MAP_STACKED_STYLE = {
  sectionGapPx: 5,
  headerGapPx: 1,
  inlineHeaderGapPx: 8,
  inlineSeparator: "·",
  inlineSeparatorGapPx: 5,
  inlineLineHeight: 1.8,
}

// Tab 19 "right-narrow" — proportion: panel narrows, names shrink, columns
// widen. (Name size lives in POLICY_MAP_BODY_OVERRIDES below.)
export const POLICY_MAP_RIGHT_NARROW_STYLE = {
  panelWidthPx: 140,
}

// Tab 20 "right-hanging" — typography: wrapping stays but reads as units —
// wrapped lines hang under the name's first character and items get their own
// inter-item space (larger than the line gap).
export const POLICY_MAP_RIGHT_HANGING_STYLE = {
  indentPx: 8,
  itemGapPx: 3,
  nameLineHeight: 1.3,
}

// Which highlight vocabulary each design speaks (the tab 7/8/9/10 kinds).
const POLICY_MAP_HIGHLIGHT_KIND: Record<
  PolicyMapDesignId,
  "red-name" | "red-chip" | "mini-square" | "underline"
> = {
  "red-name": "red-name",
  "red-chip": "red-chip",
  "mini-square": "mini-square",
  "definition-right": "underline",
  "headline-top": "red-name",
  "hud0-map-small": "red-name",
  "hud0-map-flat": "red-name",
  "hud0-map-flat-lean": "red-name",
  "hud0-lean-status-underline": "red-name",
  "hud0-map-inline": "red-name",
  "hud0-big-square": "red-name",
  "hud0-detail-large": "red-name",
  "right-rows": "underline",
  "right-inline": "underline",
  "right-narrow": "underline",
  "right-hanging": "underline",
  "underline-bottom": "underline",
}

// Per-design overrides for the map BODY's type scale and layout; unset fields
// fall back to POLICY_MAP_STYLE (three equal columns).
const POLICY_MAP_BODY_OVERRIDES: Partial<
  Record<
    PolicyMapDesignId,
    {
      headerFontSize?: string
      nameFontSize?: string
      // "sans": names render in the body face instead of mono (the power
      // breakdown's lab-row voice).
      nameFace?: "sans"
      layout?: "stacked-rows" | "stacked-inline"
    }
  >
> = {
  "hud0-map-small": { headerFontSize: "9.5px", nameFontSize: "8px" },
  "hud0-map-flat": { headerFontSize: "8.5px" },
  "hud0-map-flat-lean": { headerFontSize: "8.5px" },
  "hud0-lean-status-underline": {
    headerFontSize: POLICY_MAP_POWER_MATCHED_TYPE.headerFontSize,
    nameFontSize: POLICY_MAP_POWER_MATCHED_TYPE.nameFontSize,
    nameFace: "sans",
  },
  "hud0-map-inline": { headerFontSize: "8.5px", layout: "stacked-inline" },
  "right-rows": { layout: "stacked-rows" },
  "right-inline": { headerFontSize: "8.5px", layout: "stacked-inline" },
  "right-narrow": { nameFontSize: "8px" },
}

// The tabs whose definition is the hud-0-style detail block on top.
const POLICY_MAP_HUD0_DESIGNS: PolicyMapDesignId[] = [
  "hud0-map-small",
  "hud0-map-flat",
  "hud0-map-flat-lean",
  "hud0-lean-status-underline",
  "hud0-map-inline",
  "hud0-big-square",
  "hud0-detail-large",
]

// The tabs whose definition lives in the fixed right panel.
const POLICY_MAP_RIGHT_DESIGNS: PolicyMapDesignId[] = [
  "definition-right",
  "right-rows",
  "right-inline",
  "right-narrow",
  "right-hanging",
]

// The policy's own mini square at map-row scale (POLICY_MAP_MINI_SQUARE_STYLE
// — smaller than PolicyMiniSquare so it fits inside a name row's line box).
const PolicyMapRowSquare = ({
  policy,
  status,
}: {
  policy: Policy
  status: PolicyStatus
}) => {
  const style = POLICY_MAP_MINI_SQUARE_STYLE
  const appearance = HUD87_POLICY_STATUS_APPEARANCE[status]
  const IconComponent = policy.icon ? POLICY_ICONS[policy.icon] : undefined
  return (
    <span
      className="inline-flex items-center justify-center align-middle"
      style={{
        width: style.squareSizePx,
        height: style.squareSizePx,
        borderRadius: style.squareRadiusPx,
        background: appearance.fillColor,
        border: `${style.squareStrokeWidthPx}px ${appearance.borderStyle} ${appearance.borderColor}`,
        color: appearance.iconColor,
      }}
    >
      {IconComponent && (
        <IconComponent size={style.squareIconSizePx} strokeWidth={2.4} />
      )}
    </span>
  )
}

// One policy name (or a section header, via `header`) with the design's
// hover-highlight treatment applied when it is the hovered thing. Every
// treatment reserves its own space when idle (transparent chip padding, an
// empty marker slot, a transparent underline) so the columns never shift.
const MapHighlightText = ({
  design,
  lit,
  header = false,
  markerPolicy,
  markerStatus,
  children,
}: {
  design: PolicyMapDesignId
  lit: boolean
  header?: boolean
  // For the "mini-square" kind: the policy whose square marks the row; header
  // rows pass only markerStatus and get a plain square in the section's color.
  markerPolicy?: Policy
  markerStatus?: PolicyStatus
  children: string
}) => {
  const kind = POLICY_MAP_HIGHLIGHT_KIND[design]
  if (kind === "red-chip") {
    const chip = POLICY_MAP_RED_CHIP_STYLE
    return (
      <span
        style={{
          padding: `${chip.paddingYPx}px ${chip.paddingXPx}px`,
          marginLeft: -chip.paddingXPx,
          borderRadius: chip.borderRadiusPx,
          ...(lit && { background: chip.background, color: chip.textColor }),
        }}
      >
        {children}
      </span>
    )
  }
  if (kind === "mini-square") {
    const style = POLICY_MAP_MINI_SQUARE_STYLE
    const appearance = markerStatus
      ? HUD87_POLICY_STATUS_APPEARANCE[markerStatus]
      : undefined
    return (
      <span>
        <span
          className="inline-block"
          style={{ width: style.slotWidthPx }}
        >
          {lit &&
            (markerPolicy && markerStatus ? (
              <PolicyMapRowSquare policy={markerPolicy} status={markerStatus} />
            ) : (
              appearance && (
                <span
                  className="inline-block align-middle"
                  style={{
                    width: style.squareSizePx,
                    height: style.squareSizePx,
                    borderRadius: style.squareRadiusPx,
                    background: appearance.borderColor,
                  }}
                />
              )
            ))}
        </span>
        {children}
      </span>
    )
  }
  if (kind === "underline") {
    const style = POLICY_MAP_DEFINITION_RIGHT_STYLE
    return (
      <span
        style={{
          borderBottom: `${style.underlineWidthPx}px solid ${
            lit ? style.underlineColor : "transparent"
          }`,
        }}
      >
        {children}
      </span>
    )
  }
  // "red-name": pure color on the hovered thing.
  const color =
    design === "headline-top"
      ? POLICY_MAP_HEADLINE_TOP_STYLE.highlightColor
      : POLICY_MAP_RED_NAME_STYLE.highlightColor
  return <span style={lit ? { color } : undefined}>{children}</span>
}

// The all-policies map itself, in the design's layout: three equal columns
// (default), stacked full-width rows, or stacked inline flows.
const PolicyMapBody = ({
  design,
  hoveredPolicyId,
  hoveredStatus,
}: {
  design: PolicyMapDesignId
  hoveredPolicyId: string | null
  hoveredStatus: PolicyStatus | null
}) => {
  const grouped = useGroupedPoliciesForActiveDate()
  const overrides = POLICY_MAP_BODY_OVERRIDES[design]
  const headerFontSize = overrides?.headerFontSize ?? POLICY_MAP_STYLE.headerFontSize
  const nameFontSize = overrides?.nameFontSize ?? POLICY_MAP_STYLE.nameFontSize
  const nameFaceClass = overrides?.nameFace === "sans" ? "font-sans" : "font-mono"
  const layout = overrides?.layout

  if (layout === "stacked-inline") {
    const stacked = POLICY_MAP_STACKED_STYLE
    return (
      <div>
        {POLICY_STATUS_ORDER.map((status) => (
          <div
            key={status}
            className="font-mono"
            style={{
              fontSize: nameFontSize,
              lineHeight: stacked.inlineLineHeight,
              marginBottom: stacked.sectionGapPx,
            }}
          >
            <span
              className="font-bold"
              style={{
                fontSize: headerFontSize,
                marginRight: stacked.inlineHeaderGapPx,
              }}
            >
              <MapHighlightText
                design={design}
                lit={hoveredStatus === status}
                header
                markerStatus={status}
              >
                {POLICY_STATUS_LABELS[status]}
              </MapHighlightText>
            </span>
            {grouped[status].length === 0
              ? POLICY_MAP_EMPTY_SECTION_MARK
              : grouped[status].map((policy, i) => (
                  <span key={policy.id}>
                    <span className="inline-block whitespace-nowrap">
                      <MapHighlightText
                        design={design}
                        lit={hoveredPolicyId === policy.id}
                        markerPolicy={policy}
                        markerStatus={status}
                      >
                        {policy.label}
                      </MapHighlightText>
                    </span>
                    {i < grouped[status].length - 1 && (
                      <span
                        style={{
                          margin: `0 ${stacked.inlineSeparatorGapPx}px`,
                        }}
                      >
                        {stacked.inlineSeparator}
                      </span>
                    )}
                  </span>
                ))}
          </div>
        ))}
      </div>
    )
  }

  const hanging = design === "right-hanging"
  const nameRowStyle = {
    fontSize: nameFontSize,
    lineHeight: hanging
      ? POLICY_MAP_RIGHT_HANGING_STYLE.nameLineHeight
      : POLICY_MAP_STYLE.nameLineHeight,
    ...(hanging && {
      textIndent: -POLICY_MAP_RIGHT_HANGING_STYLE.indentPx,
      paddingLeft: POLICY_MAP_RIGHT_HANGING_STYLE.indentPx,
      marginBottom: POLICY_MAP_RIGHT_HANGING_STYLE.itemGapPx,
    }),
  }

  // "hud0-lean-status-underline": the hovered status's header keeps its
  // black text and is marked by a red rule beneath instead (the power
  // breakdown's underline emphasis). The rule's slot renders on EVERY header
  // (transparent when idle), so columns never shift as the hover moves.
  const statusUnderline = design === "hud0-lean-status-underline"
  const section = (status: PolicyStatus) => (
    <>
      <div
        className="font-mono font-bold"
        style={{ fontSize: headerFontSize }}
      >
        <MapHighlightText
          design={design}
          lit={!statusUnderline && hoveredStatus === status}
          header
          markerStatus={status}
        >
          {POLICY_STATUS_LABELS[status]}
        </MapHighlightText>
      </div>
      {statusUnderline && (
        <div
          style={{
            height: POLICY_MAP_STATUS_UNDERLINE_STYLE.ruleWidthPx,
            marginTop: POLICY_MAP_STATUS_UNDERLINE_STYLE.gapPx,
            background:
              hoveredStatus === status
                ? POLICY_MAP_STATUS_UNDERLINE_STYLE.ruleColor
                : "transparent",
          }}
        />
      )}
      <div style={{ marginTop: POLICY_MAP_STYLE.headerGapPx }}>
        {grouped[status].length === 0 ? (
          <div className={nameFaceClass} style={nameRowStyle}>
            {POLICY_MAP_EMPTY_SECTION_MARK}
          </div>
        ) : (
          grouped[status].map((policy) => (
            <div key={policy.id} className={nameFaceClass} style={nameRowStyle}>
              <MapHighlightText
                design={design}
                lit={hoveredPolicyId === policy.id}
                markerPolicy={policy}
                markerStatus={status}
              >
                {policy.label}
              </MapHighlightText>
            </div>
          ))
        )}
      </div>
    </>
  )

  if (layout === "stacked-rows") {
    return (
      <div>
        {POLICY_STATUS_ORDER.map((status) => (
          <div
            key={status}
            style={{ marginBottom: POLICY_MAP_STACKED_STYLE.sectionGapPx }}
          >
            {section(status)}
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="flex" style={{ gap: POLICY_MAP_STYLE.columnGapPx }}>
      {POLICY_STATUS_ORDER.map((status) => (
        <div key={status} className="flex-1 min-w-0">
          {section(status)}
        </div>
      ))}
    </div>
  )
}

// The full map: all policies plus the hovered item's definition in the
// design's reserved spot. Rendered by the below-HUD detail slot for BOTH
// hover kinds in the policy area — a policy square (hoveredPolicyId) and a
// status label (hoveredStatus).
export const PolicyMapHoverDetail = ({
  design,
  hoveredPolicyId,
  hoveredStatus,
}: {
  design: PolicyMapDesignId
  hoveredPolicyId: string | null
  hoveredStatus: PolicyStatus | null
}) => {
  const grouped = useGroupedPoliciesForActiveDate()
  // Non-stock rule treatments reserve the tighter one-line header footprint
  // (nonStockDetailMinHeightPx) so the map sits 15px higher.
  const stockRule = useContext(HudHoverRuleTreatmentContext) === "stock"
  const hoveredPolicyStatus = hoveredPolicyId
    ? POLICY_STATUS_ORDER.find((s) =>
        grouped[s].some((p) => p.id === hoveredPolicyId),
      )
    : undefined
  const hoveredPolicy =
    hoveredPolicyId && hoveredPolicyStatus
      ? grouped[hoveredPolicyStatus].find((p) => p.id === hoveredPolicyId)
      : undefined

  // What the definition area shows: the hovered policy's name + description,
  // or — for a status hover — the per-status caption ("hud0-lean-status-
  // underline" shows the ONE broad AI-Policies definition for every status
  // instead).
  const broadStatusDefinition = design === "hud0-lean-status-underline"
  const definitionName = hoveredPolicy
    ? hoveredPolicy.label
    : hoveredStatus
      ? broadStatusDefinition
        ? POLICY_MAP_AI_POLICIES_COPY.title
        : POLICY_STATUS_LABELS[hoveredStatus]
      : undefined
  const definitionText = hoveredPolicy
    ? POLICY_DESCRIPTIONS[hoveredPolicy.id]
    : hoveredStatus
      ? broadStatusDefinition
        ? POLICY_MAP_AI_POLICIES_COPY.definition
        : POLICY_MAP_STATUS_CAPTIONS[hoveredStatus]
      : undefined

  const mapBody = (
    <PolicyMapBody
      design={design}
      hoveredPolicyId={hoveredPolicyId}
      hoveredStatus={hoveredStatus}
    />
  )

  const definitionBody = definitionName && (
    <>
      <div className="flex items-start gap-1.5">
        {hoveredPolicy && hoveredPolicyStatus && (
          <PolicyMiniSquare
            policy={hoveredPolicy}
            status={hoveredPolicyStatus}
          />
        )}
        <div
          className="font-mono font-bold"
          style={{ fontSize: POLICY_MAP_STYLE.definitionNameFontSize }}
        >
          {definitionName}
        </div>
      </div>
      {definitionText && (
        <div
          className="font-sans mt-0.5"
          style={{
            fontSize: POLICY_MAP_STYLE.definitionFontSize,
            lineHeight: POLICY_MAP_STYLE.definitionLineHeight,
          }}
        >
          {definitionText}
        </div>
      )}
    </>
  )

  if (POLICY_MAP_HUD0_DESIGNS.includes(design)) {
    const style = POLICY_MAP_HUD0_TOP_STYLE
    const big = design === "hud0-big-square"
    // Tab 22 (+ the status-underline variant): no eyebrow, and the
    // description stays the original one-liner.
    const lean =
      design === "hud0-map-flat-lean" || design === "hud0-lean-status-underline"
    // The hud-0 detail's full text: base description + the extended sentence.
    const fullText = hoveredPolicy
      ? [
          POLICY_DESCRIPTIONS[hoveredPolicy.id],
          ...(lean ? [] : [POLICY_EXTENDED_DESCRIPTIONS[hoveredPolicy.id]]),
        ]
          .filter(Boolean)
          .join(" ")
      : definitionText
    const eyebrow = !lean && hoveredPolicy && hoveredPolicyStatus && (
      <div className="font-mono text-[8.5px] mb-1.5">
        {POLICY_STATUS_LABELS[hoveredPolicyStatus]}
      </div>
    )
    // Title + description render in the shared hover-detail opener
    // (HudHoverTitle / HudHoverDescription), so the policy detail reads as
    // one system with the power breakdown and the other hover menus.
    const description = fullText && (
      <HudHoverDescription className="max-w-[46em]">{fullText}</HudHoverDescription>
    )
    const detail = definitionName && (
      big && hoveredPolicy && hoveredPolicyStatus ? (
        // Poster square left, text column (eyebrow / name / description)
        // beside it — the tab-23 big-square-aside arrangement.
        <div
          className="flex items-start"
          style={{ gap: POLICY_MAP_HUD0_BIG_SQUARE_STYLE.textGapPx }}
        >
          <PolicyMapPosterSquare
            policy={hoveredPolicy}
            status={hoveredPolicyStatus}
          />
          <div>
            {eyebrow}
            <HudHoverTitle>{definitionName}</HudHoverTitle>
            {description}
          </div>
        </div>
      ) : (
        <div>
          {eyebrow}
          <div className="flex items-start gap-1.5">
            {hoveredPolicy && hoveredPolicyStatus && (
              <PolicyMiniSquare
                policy={hoveredPolicy}
                status={hoveredPolicyStatus}
              />
            )}
            <HudHoverTitle>{definitionName}</HudHoverTitle>
          </div>
          {description}
        </div>
      )
    )
    return (
      <div>
        <div
          style={{
            minHeight:
              design === "hud0-lean-status-underline"
                ? stockRule
                  ? POLICY_MAP_STATUS_UNDERLINE_STYLE.detailMinHeightPx
                  : POLICY_MAP_STATUS_UNDERLINE_STYLE.nonStockDetailMinHeightPx
                : lean
                  ? POLICY_MAP_HUD0_LEAN_STYLE.detailMinHeightPx
                  : style.detailMinHeightPx,
          }}
        >
          {detail}
        </div>
        <HudHoverDetailBody>{mapBody}</HudHoverDetailBody>
      </div>
    )
  }

  if (POLICY_MAP_RIGHT_DESIGNS.includes(design)) {
    const style = POLICY_MAP_DEFINITION_RIGHT_STYLE
    const panelWidthPx =
      design === "right-narrow"
        ? POLICY_MAP_RIGHT_NARROW_STYLE.panelWidthPx
        : style.panelWidthPx
    return (
      <div className="flex items-start" style={{ gap: style.panelGapPx }}>
        <div className="flex-1 min-w-0">{mapBody}</div>
        <div className="shrink-0" style={{ width: panelWidthPx }}>
          {definitionBody}
        </div>
      </div>
    )
  }

  if (design === "headline-top") {
    const style = POLICY_MAP_HEADLINE_TOP_STYLE
    return (
      <div>
        <div style={{ minHeight: style.minHeightPx }}>
          {definitionName && (
            <div className="flex items-baseline gap-2">
              <div
                className="font-mono font-bold whitespace-nowrap"
                style={{ fontSize: style.nameFontSize }}
              >
                {definitionName}
              </div>
              {definitionText && (
                <div
                  className="font-sans"
                  style={{
                    fontSize: POLICY_MAP_STYLE.definitionFontSize,
                    lineHeight: POLICY_MAP_STYLE.definitionLineHeight,
                  }}
                >
                  {definitionText}
                </div>
              )}
            </div>
          )}
        </div>
        <div
          style={{
            height: style.ruleWidthPx,
            background: style.ruleColor,
            marginBottom: style.columnsGapPx,
            marginTop: style.ruleGapPx,
          }}
        />
        {mapBody}
      </div>
    )
  }

  // "red-name" / "red-chip" / "mini-square" / "underline-bottom": definition
  // strip BELOW the columns, its height reserved so the map never reflows as
  // the hover moves.
  return (
    <div>
      {mapBody}
      <div
        style={{
          marginTop: POLICY_MAP_STYLE.definitionGapPx,
          minHeight: POLICY_MAP_STYLE.definitionMinHeightPx,
        }}
      >
        {definitionBody}
      </div>
    </div>
  )
}

// The tab-15 poster-scale square (POLICY_MAP_HUD0_BIG_SQUARE_STYLE) — the
// same per-status treatment as the grid squares, icon included.
const PolicyMapPosterSquare = ({
  policy,
  status,
}: {
  policy: Policy
  status: PolicyStatus
}) => {
  const style = POLICY_MAP_HUD0_BIG_SQUARE_STYLE
  const appearance = HUD87_POLICY_STATUS_APPEARANCE[status]
  const IconComponent = policy.icon ? POLICY_ICONS[policy.icon] : undefined
  return (
    <span
      className="flex items-center justify-center shrink-0"
      style={{
        width: style.squareSizePx,
        height: style.squareSizePx,
        borderRadius: style.squareRadiusPx,
        background: appearance.fillColor,
        border: `${style.squareStrokeWidthPx}px ${appearance.borderStyle} ${appearance.borderColor}`,
        color: appearance.iconColor,
      }}
    >
      {IconComponent && (
        <IconComponent size={style.squareIconSizePx} strokeWidth={1.9} />
      )}
    </span>
  )
}
